import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Stats from './components/Stats';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { ListFilters } from '../../../../interfaces/customer.interface';
import { buildHeadCells } from '../../../../utils/methods';
import { GENERIC_LOOKUP_KEYS, VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING } from '../../../../utils/appConstant';
import useSorting from '../../../../hooks/useSorting';
import usePagination from '../../../../hooks/usePagination';
import useUrlParams from '../../../../hooks/useUrlParams';
import useFetch from '../../../../hooks/useFetch';
import CustomerService from '../../../../services/customer.service';
import CommonService from '../../../../services/common.service';
import { SortOrder, Modules, AuthRoutes } from '../../../../enums';
import { usePersistentFilters } from '../../../../hooks/usePersistentFilters';

const List: React.FC = () => {
  const { t } = useTranslation();

  const defaultFilters: ListFilters = {
    searchText: '',
    city: '',
    status: '1'
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_customers', defaultFilters);

  // Initialize list state with persisted filters
  const [filters, setFilters] = useState<ListFilters>(initialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    status: '',
    city: ''
  });

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.primaryContact,
    VALIDATION_FIELD_MAPPING.primaryEmail,
    VALIDATION_FIELD_MAPPING.primaryPhone,
    VALIDATION_FIELD_MAPPING.city,
    VALIDATION_FIELD_MAPPING.status,
    VALIDATION_FIELD_MAPPING.actions
  ], 'customerList');

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const {
    order, orderBy, setOrder, setOrderBy,
  } = useSorting({ defaultOrder: searchParams.get('order') as SortOrder || SortOrder.asc, defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.primaryContact });
  const {
    page, perPage, setPage, setPerPage,
  } = usePagination({ defaultPage: Number(searchParams.get('page')) || 1 });

  useUrlParams({
    type: AuthRoutes.customers, uri: Modules.resources + AuthRoutes.customers, perPage, page, order, orderBy
  });

  const { total, data: customers, loading } = useFetch({
    service: CustomerService,
    func: 'list',
    payload: {
      page, perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.customerList as any)[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      status: (filters.status === '1') ? 1 : (filters.status.toLowerCase() === '0' ? 0 : ""),
      city: filters.city
    },
    dependency: [order, orderBy, page, perPage, filters.searchText, filters.status, filters.city]
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: dropdownSearchData.city },
    dependency: [dropdownSearchData.city]
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: []
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status]
  });

  const { data: kpis } = useFetch({
    service: CustomerService,
    func: 'kpis',
    payload: null,
    dependency: []
  });

  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters(defaultFilters);
    resetFilters();
  }, [resetFilters, setPage]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const headerSection = <TableHeaderSection total={total} />

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection, row: Row, data: customers || [], pagination: true, headCells, order, orderBy, page, perPage, total, loading, noDataFoundMessage: t("customers.list.pageTexts.table.noDataFoundMessage"), setPage, setPerPage, setOrder, setOrderBy
    };
  }, [headCells, order, orderBy, page, perPage, total, loading, customers, setPage, setPerPage, setOrder, setOrderBy]);

  return (
    <>
      <Header />

      <Stats data={kpis} />

      <Filters
        status={status}
        cities={cities}
        allCities={allCities}
        allStatus={allStatus}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        initialFilters={initialFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <TableBox
        tableValues={values}
      />
    </>
  );
};

export default List;
